import { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import Divider from "./Divider";
import Button from "./Button";
import "./DataTable.css";

export default function DataTable({
  title,
  label,
  description,
  data,
  columns,
  toolbarLeft,
  toolbarRight,
  headerActions,
  rowActions, // 👈 function(row) => JSX
}) {
  const [sorting, setSorting] = useState([]);

  // Inject an extra "actions" column if rowActions is supplied
  const columnsWithActions = rowActions
    ? [
      ...columns,
      {
        id: "actions",
        header: "",
        cell: (info) => rowActions(info.row.original),
      },
    ]
    : columns;

  const table = useReactTable({
    data,
    columns: columnsWithActions,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="table-wrapper">
      {/* --- Header --- */}
      <div className="table-header">
        <div className="table-header-left">
          <h2 className="table-title">
            {title}
            {label && <span className="table-label">{label}</span>}
          </h2>
          {description && <p className="table-description">{description}</p>}
        </div>
        <div className="table-header-right">
          {headerActions}
        </div>
      </div>

      <Divider />

      {/* Optional toolbar (filters/search etc.) */}
      {(toolbarLeft || toolbarRight) && (
        <div className="table-toolbar">
          <div className="left-controls">{toolbarLeft}</div>
          <div className="right-controls">{toolbarRight}</div>
        </div>
      )}

      <Divider />

      {/* --- Table --- */}
      <table className="styled-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                    width: header.column.columnDef.meta?.width,
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: " 🔼",
                    desc: " 🔽",
                  }[header.column.getIsSorted()] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{ width: cell.column.columnDef.meta?.width }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- Footer --- */}
      <div className="table-footer">
        <span>
          Pagina {table.getState().pagination.pageIndex + 1} van {table.getPageCount()}
        </span>
        <div className="pagination-buttons">
          <Button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} variant="secondary">
            Vorige
          </Button>
          <Button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} variant="secondary">
            Volgende
          </Button>
        </div>
      </div>
    </div>
  );
}